import { createAsyncThunk } from '@reduxjs/toolkit';
import { ColumnFiltersState } from '@tanstack/react-table';
import { Axios } from 'api/axios';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  'Content-Type': 'application/json',
};

export interface Department {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchDepartment = createAsyncThunk(
  'department/fetchDepartment',
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: 'asc' | 'desc' | '';
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search?.trim() || "";


      if (sort) {
        params.ordering = order === 'desc' ? `-${sort}` : sort;
      }

     if (filters && Array.isArray(filters)) {
        filters.forEach((f: any) => {
          const { id, value } = f
          if (!id || value === undefined || value === '') return

          // Date-range filters: create e.g. created_at_date_from / created_at_date_to
          if ((id === 'updated_at' || id === 'created_at') && value && typeof value === 'object') {
            const dateFilter = value as { from_date?: string; to_date?: string }
            if (dateFilter.from_date) params[`${id}_date_from`] = dateFilter.from_date
            if (dateFilter.to_date) params[`${id}_date_to`] = dateFilter.to_date
            return
          }

          // If value is an object (non-date), flatten as id_key=value (e.g., status_eq=active)
          if (value && typeof value === 'object') {
            Object.entries(value).forEach(([k, v]) => {
              if (v !== undefined && v !== null && v !== '') {
                params[`${id}_${k}`] = v
              }
            })
            return
          }

          // Default scalar filter
          params[id] = value
        })
      }
      
      const response: any = await Axios.get(`api/department/`, { params });

      const responseData = response.data;

      return {
        data: responseData?.data || responseData?.results || [],
        rowCount: responseData?.count || 0
      };
    } catch (error: any) {
      console.error('fetchDepartment error:', error.response?.data || error.message);
      throw new Error('Failed to fetch departments');
    }
  }
);


export const fetchById = createAsyncThunk(
  'department/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`api/department/${id}/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to fetch department by ID'
      );
    }
  }
);


export const updateDepartment = createAsyncThunk(
  'department/updateDepartment',
  async (depData: any) => {
    if (!depData?.department_id) throw new Error('department_id is required');

    const response = await Axios.patch(
      `api/department/edit-status/${depData.department_id}/`, // <- added slash
      depData
    );

    return response.data;
  }
);
export const updateStatusInDepartment = createAsyncThunk(
  'department/updateStatusInDepartment',
  async (data: any) => {
    const departmentId = data.department_id || data.departmentId;
    console.log('departmentId:', departmentId);
    console.log('Payload:', data);

    if (!departmentId) {
      throw new Error('department_id is missing');
    }

    const response = await Axios.patch(
      `api/department/edit-status/${departmentId}/`, // <- added slash
      data
    );
    return response.data;
  }
);


export const addDepartment = createAsyncThunk(
  'department/addDepartment',
  async (packageData: any) => {
    const response = await Axios.post(`${API_URL}/api/department/`, packageData);
    return response.data;
  }
);
export const createDepartment = createAsyncThunk(
  'department/createDepartment',
  async (
    { organization, department_name, head_of_department }: { organization: string; department_name: string, head_of_department: string },
    { rejectWithValue }
  ) => {
    try {
      const userData = localStorage.getItem('authUser');
      const token = userData ? JSON.parse(userData).access : '';
      const response = await Axios.post(
        'api/department/create/',
        { organization, department_name, head_of_department },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || err.message || 'Failed to create department'
      );
    }
  }
);
export const deleteDepartment = createAsyncThunk(
  'department/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await Axios.delete(`api/department/${id}/`, { headers });
      return id;
    } catch (err: any) {
      console.error('Delete error:', err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchOrgDropDown = createAsyncThunk(
  'department/fetchOrgDropDown',
  async () => {
    try {
      const response = await Axios.get(
        `/api/user/organization-dropdown/`, // <-- add leading slash
        { headers }
      );
      // Return only the array of organization names
      return response?.data?.data;
    } catch (err) {
      console.error('fetchOrgDropDown error:', err);
      throw err;
    }
  }
);
export const fetchAllDepartmentsDropdowns = createAsyncThunk(
  'organization/fetchAllDepartmentsDropdowns',
  async () => {
    try {
      console.log("hd :",headers);
      const [departmentRes, orgRes] = await Promise.all([
        Axios.get(`api/department/get-all/`, { headers }),
        Axios.get(`api/organization/all-org/`, { headers })
      ]);

      console.log("Departments response:", departmentRes.data.data);
      console.log("Organizations response:", orgRes.data.data);

      return {
        orgDropDown: Array.isArray(orgRes.data.data)
          ? orgRes.data.data.map((item: any) => item.organization_name)
          : [],
        departDropdown: Array.isArray(departmentRes.data.data)
          ? Array.from(new Set(departmentRes.data.data.map((d: any) => d.department_name)))
          : []

      };
    } catch (err) {
      console.error("fetchAllDepartmentsDropdowns error:", err);
      throw err;
    }
  }
)

export const exportDepartmentsCSV = createAsyncThunk(
  'department/exportCSV',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': '*/*'
      };
      // Use responseType: 'blob' to get the file as a blob
      const response = await Axios.get(
        'api/department/?export=excel',
        { headers, responseType: 'blob' }
      );
      // Return the blob and filename (if any)
      return {
        blob: response.data,
        filename:
          response.headers['content-disposition']
            ?.split('filename=')[1]
            ?.replace(/"/g, '') || 'departments.csv'
      };
    } catch (err: any) {
      console.error('Export CSV error:', err);
      return rejectWithValue(err.message || 'Failed to export CSV');
    }
  }
);


export const DepartmentExportsCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();
  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(({ id, value }: { id: string; value: any }) => {
      if (id && value !== undefined && value !== null) {
        if (typeof value === "object" && value !== null) {
          const maybeDateFilter = value as {
            from_date?: string;
            to_date?: string;
          };

          if ((maybeDateFilter.from_date || maybeDateFilter.to_date) &&
              (id === "created_at" || id === "updated_at")) {
            if (maybeDateFilter.from_date) {
              params.append(`${id}_from`, maybeDateFilter.from_date);
            }
            if (maybeDateFilter.to_date) {
              params.append(`${id}_to`, maybeDateFilter.to_date);
            }
            return;
          }

          Object.entries(value).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== "") {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(id, String(value));
        }
      }
    });
  }

  console.log("Exporting department URL:", `/department/?${params.toString()}`);

  const response = await Axios.get(`api/department/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Department.csv",
  };
};


